import {
  CircleCheckIcon,
  InfoIcon,
  Loader2Icon,
  OctagonXIcon,
  TriangleAlertIcon,
} from 'lucide-react';
import { Toaster as Sonner, type ToasterProps } from 'sonner';
import { cn } from '@/lib/utils';

/**
 * shadcn's sonner component. Two departures from the registry copy, both forced
 * by this project rather than chosen:
 *
 * - The theme arrives as a prop instead of from next-themes, which is not a
 *   dependency here; the caller reads it from the Redux ui slice.
 * - The tokens are HSL triplets, so each is wrapped in hsl(). The registry
 *   assumes v4 @theme tokens that already hold a full colour.
 *
 * Feeding sonner's own variables is the whole point of this shape: its
 * stylesheet is injected at runtime and therefore unlayered, so it outranks
 * anything in @layer utilities no matter how specific. Overriding it with
 * classes does not work, which is what the !important block this replaces was
 * compensating for.
 */
const TOKENS = {
  '--normal-bg': 'hsl(var(--popover))',
  '--normal-text': 'hsl(var(--popover-foreground))',
  '--normal-border': 'hsl(var(--border))',
  '--border-radius': 'var(--radius)',

  // richColors otherwise paints from sonner's own greens and reds, which are
  // not in this palette. Each tint is the severity token at 12% over the
  // popover surface, so it stays a surface rather than a block of colour, and
  // the text is the full-strength token.
  '--success-bg': 'color-mix(in srgb, hsl(var(--success)) 12%, hsl(var(--popover)))',
  '--success-text': 'hsl(var(--success))',
  '--success-border': 'color-mix(in srgb, hsl(var(--success)) 30%, hsl(var(--border)))',

  '--error-bg': 'color-mix(in srgb, hsl(var(--destructive)) 12%, hsl(var(--popover)))',
  '--error-text': 'hsl(var(--destructive))',
  '--error-border': 'color-mix(in srgb, hsl(var(--destructive)) 30%, hsl(var(--border)))',

  '--warning-bg': 'color-mix(in srgb, hsl(var(--warning)) 12%, hsl(var(--popover)))',
  '--warning-text': 'hsl(var(--warning))',
  '--warning-border': 'color-mix(in srgb, hsl(var(--warning)) 30%, hsl(var(--border)))',

  '--info-bg': 'color-mix(in srgb, hsl(var(--primary)) 12%, hsl(var(--popover)))',
  '--info-text': 'hsl(var(--primary))',
  '--info-border': 'color-mix(in srgb, hsl(var(--primary)) 30%, hsl(var(--border)))',

  // Sonner pins the close control to the *leading* corner as a disc half
  // outside the toast. These are its own hooks for putting it on the trailing
  // corner instead — the same treatment, mirrored. Sitting outside means the
  // row no longer has to reserve a lane for it, which is the space the message
  // and the action button get back.
  '--toast-close-button-start': 'auto',
  '--toast-close-button-end': '0',
  '--toast-close-button-transform': 'translate(35%, -35%)',
} as React.CSSProperties;

const Toaster = ({ className, style, ...props }: ToasterProps) => {
  return (
    <Sonner
      className={cn('toaster group', className)}
      icons={{
        success: <CircleCheckIcon className="size-5" />,
        info: <InfoIcon className="size-5" />,
        warning: <TriangleAlertIcon className="size-5" />,
        error: <OctagonXIcon className="size-5" />,
        loading: <Loader2Icon className="size-5 animate-spin" />,
      }}
      // Merged rather than spread over: the registry lets `{...props}` win, but
      // a caller passing style for something unrelated — a z-index, here —
      // would drop the tokens above and take the styling with them.
      style={{ ...TOKENS, ...style }}
      {...props}
    />
  );
};

export { Toaster };
